import {
  Results as ReactifySearchResults,
  ResultsProps as ReactifySearchResultsProps,
  Stats,
  useResults,
} from "@usereactify/search";
import React, { useContext, useEffect, useMemo } from "react";

import { ColorGroup } from "../../reactify";
import { GridLayout, ViewSettingsContext } from "../../context/ViewSettingsContext";
import { ResultStatsContext } from "../../context/ResultStatsContext";
import { SkeletonGrid } from "../Skeleton/SkeletonGrid";
import { Sort } from "../Sort/Sort";
import { NoResults } from "./NoResults";
import { ResultsCallout } from "./ResultsCallout";
import { ResultsError } from "./ResultsError";
import { ResultsItem } from "./ResultsItem";

export const Results: React.FC<{
  colorGroups: ColorGroup[] | null;
  pageSize?: number;
}> = (props) => {
  const { colorGroups, pageSize } = props;
  const { displayedResults, setDisplayedResults, numberOfResults, setNumberOfResults } = useContext(ResultStatsContext);
  const viewSettings = useContext(ViewSettingsContext);
  const results = useResults();

  if (!viewSettings) {
    throw new Error("ViewSettingsContext must be used within a ViewSettingsProvider");
  }

  const { gridLayout, setGridLayout } = viewSettings;

  useEffect(() => {
    if (results && results.resultStats) {
      const newDisplayed = results.resultStats.displayedResults || null;
      const newTotal = results.resultStats.numberOfResults || null;

      if (newDisplayed !== displayedResults) {
        setDisplayedResults(newDisplayed);
      }
      if (newTotal !== numberOfResults) {
        setNumberOfResults(newTotal);
      }
    }
  }, [results, setDisplayedResults, setNumberOfResults, displayedResults, numberOfResults]);

  return (
    <>
      <ResultsHeader gridLayout={gridLayout} setGridLayout={setGridLayout} />

      <ReactifySearchResults
        pageSize={pageSize}
        listClassName={`new-grid product-grid collection-grid view-${gridLayout}`}
        renderNoResults={NoResults}
        data-view={`${gridLayout}`}
        renderError={ResultsError}
        renderLoading={SkeletonGrid}
        renderResultCardCallout={ResultsCallout}
        renderResultCardProduct={ResultsItem}
        renderPaginationLoadMore={LoadMoreBtn}
      />
    </>
  );
};

const StatsRender: React.FC = () => {
  return (
    <Stats
      render={(renderProps) => {
        if (renderProps.resultStats.numberOfResults === 0) return <></>;
        return <>{renderProps.resultStats.numberOfResults} products</>;
      }}
    />
  );
};

const ResultsHeader: React.FC<{
  gridLayout: GridLayout;
  setGridLayout: (layout: GridLayout) => void;
}> = (props) => {
  const isMediumView = useMemo(() => props.gridLayout === GridLayout.MEDIUM, [props.gridLayout]);
  const isLargeView = useMemo(() => props.gridLayout === GridLayout.LARGE, [props.gridLayout]);
  const isListView = useMemo(() => props.gridLayout === GridLayout.LIST, [props.gridLayout]);

  return (
    <>
      <p className="mb-0 medium-up--hide product-count">
        <StatsRender />
      </p>
      <div className="filters-bar">
        <div className="collection-filter__item collection-filter__item--left small--hide">
          <p className="mb-0 ">
            <StatsRender />
          </p>
        </div>

        <div className="collection-filter__item collection-filter__item--right">
          <div className="sort-box small--hide">
            <Sort />
          </div>
          <ul className="no-bullets inline-list text-right">
            <li>
              <button
                type="button"
                className={isLargeView ? `grid-view-btn is-active` : `grid-view-btn`}
                onClick={() => props.setGridLayout(GridLayout.LARGE)}
                data-view="large"
                title="Large"
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  role="presentation"
                  className="icon icon-view-large"
                  viewBox="0 0 35 35"
                >
                  <path d="M0 0h35v35H0z"></path>
                </svg>
                <span className="icon__fallback-text">Large</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={isMediumView ? `grid-view-btn is-active` : `grid-view-btn`}
                onClick={() => props.setGridLayout(GridLayout.MEDIUM)}
                data-view="medium"
                title="Small"
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  role="presentation"
                  className="icon icon-view-small"
                  viewBox="0 0 35 35"
                >
                  <path d="M0 0h15v15H0zM20 0h15v15H20zM0 20h15v15H0zM20 20h15v15H20z"></path>
                </svg>
                <span className="icon__fallback-text">Small</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={isListView ? `grid-view-btn is-active` : `grid-view-btn`}
                data-view="list"
                onClick={() => props.setGridLayout(GridLayout.LIST)}
                title="List"
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  role="presentation"
                  className="icon icon-view-list"
                  viewBox="0 0 35 35"
                >
                  <g id="Layer_2" data-name="Layer 2">
                    <path d="M0 0h35v8H0zM0 13h35v8H0zM0 27h35v8H0z"></path>
                  </g>
                </svg>
                <span className="icon__fallback-text">List</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

const LoadMoreBtn: React.FC<
  React.ComponentProps<NonNullable<ReactifySearchResultsProps["renderPaginationLoadMore"]>>
> = (props) => {
  const { resultStats, handleLoadMore } = props;

  return props.hasMore ? (
    <div className="container flex justify-center pt-8">
      <button className="btn px-8" onClick={handleLoadMore}>
        Load More
      </button>
    </div>
  ) : null;
};
